import { cloneElement, Suspense, useMemo } from 'react';
import { Button, Header, HStack, Link, Text, VStack } from '@direct-frontend/components';
import { useParams } from '@direct-frontend/modules-router';
import { add } from '@adv-frontend/yastore-modules/lib';
import { getOrderDeliveryMethod, isOrderEditable } from '@/entities/order';
import { CopyButton } from '@/features/CopyButton';
import type { OrderDto } from '@/shared/api/order';
import { OrderStatuses } from '@/shared/api/order';
import { Footer } from '@/shared/components/Footer';
import { formatPrice } from '@/shared/lib/formatPrice';
import { useMessageToast } from '@/shared/lib/hooks';
import { pluralize } from '@/shared/lib/pluralize';
import { getRequestContext } from '@/shared/lib/requestContext';
import { AddressInfo } from './components/AddressInfo';
import { CheckoutCharacteristic, CheckoutCharacteristicGroup } from './components/CheckoutCharacteristic';
import { CheckoutFeedbackChunk } from './components/CheckoutFeedbackChunk';
import { DeliveryInterval } from './components/DeliveryInterval';
import { Recommendations } from './components/Recommendations';
import { getCheckoutFeedbackIcon } from './lib';
import styles from './CheckoutFeedback.module.css';

export interface CheckoutFeedbackProps {
    order: OrderDto;
}

export const CheckoutFeedback = ({ order }: CheckoutFeedbackProps) => {
    const {
        company: { companyShopUrl },
        signature,
    } = getRequestContext();
    const { storeSlug = '' } = useParams();

    const { showToast } = useMessageToast({ position: 'top-center' });

    const onCopySuccess = () => {
        showToast({ title: 'Номер заказа скопирован' });
    };

    const deliveryChunks = useMemo(
        () => order.delivery_chunks.filter((chunk) => chunk.is_available),
        [order.delivery_chunks]
    );

    if (isOrderEditable(order)) {
        return null;
    }

    const finalPrice = add(order.total_final_price, order?.delivery?.final_customer_costs || 0);
    const isMultiChunks = deliveryChunks.length > 1;

    return (
        <VStack className={styles.root}>
            <VStack gap="16" alignItems="stretch">
                {cloneElement(getCheckoutFeedbackIcon(order), { className: styles.illustation })}

                <VStack gap="8" alignItems="center">
                    <Header level="h2" textAlign="center">
                        {order.status === OrderStatuses.CANCELLED ? 'Заказ отменён' : 'Заказ оформлен'}
                    </Header>

                    <Text size="mc" bold textAlign="center">
                        Отправили информацию о заказе вам на почту ({order.client.email})
                    </Text>
                </VStack>
            </VStack>
            <VStack gap="8" alignItems="stretch">
                <CheckoutCharacteristicGroup>
                    <HStack
                        className={styles.characteristic}
                        alignItems="center"
                        justifyContent="space-between"
                        gap="8"
                    >
                        <Text size="m" role="supplementary" className={styles.characteristicTitle}>
                            Номер заказа
                        </Text>
                        <CopyButton text={order.order_number} onSuccess={onCopySuccess}>
                            <Text size="m" bold>
                                {order.order_number}
                            </Text>
                        </CopyButton>
                    </HStack>
                </CheckoutCharacteristicGroup>

                <CheckoutCharacteristicGroup>
                    <CheckoutCharacteristic title="Сумма заказа" content={formatPrice(finalPrice)} />
                </CheckoutCharacteristicGroup>

                <CheckoutCharacteristicGroup>
                    <CheckoutCharacteristic title="Способ получения" content={getOrderDeliveryMethod(order)} />
                    <AddressInfo order={order} />
                </CheckoutCharacteristicGroup>

                {isMultiChunks && (
                    <CheckoutCharacteristicGroup>
                        <VStack gap="8">
                            <Text size="mc" bold>
                                Доставим {pluralize(deliveryChunks.length, '%d посылка', '%d посылки', '%d посылок')}
                            </Text>

                            <Text size="mc" role="supplementary">
                                Товары отправляются с разных складов
                            </Text>
                        </VStack>
                    </CheckoutCharacteristicGroup>
                )}
            </VStack>
            {deliveryChunks.map((chunk, index) => (
                <CheckoutFeedbackChunk
                    key={chunk.id}
                    items={chunk.items}
                    title={isMultiChunks ? `Посылка ${index + 1}` : 'Состав заказа'}
                >
                    {order.status === OrderStatuses.CANCELLED ? null : (
                        <CheckoutCharacteristicGroup>
                            <DeliveryInterval
                                method={order.delivery.method}
                                deliveryChunks={deliveryChunks}
                                chunkIndex={index}
                            />
                        </CheckoutCharacteristicGroup>
                    )}
                </CheckoutFeedbackChunk>
            ))}
            <Button className={styles.button} as={Link} href={companyShopUrl} color="accent" width="max" size="m">
                Перейти в магазин
            </Button>
            <Suspense fallback={null}>
                <Recommendations orderId={order.id} signature={signature} storeSlug={storeSlug} />
            </Suspense>
            <Footer centered />
        </VStack>
    );
};
